/**
 * Browser-side PostHog wrapper. Lazy loads `posthog-js` so it can be imported
 * from server modules without crashing the server bundle.
 *
 * Use `initBrowserPostHog()` once on app mount, then call `capture()` /
 * `identify()` from anywhere on the client.
 */
import type { PostHog as PostHogJs } from 'posthog-js';
import {
  POSTHOG_BROWSER_KEY,
  POSTHOG_HOST,
  isPostHogEnabled,
} from './config';
import { isExcludedTrackingEmail } from './excluded';
import type { CaptureContext, EventProperties, IdentifyTraits } from './types';

type PostHogClient = PostHogJs & {
  capture: PostHogJs['capture'];
};

let posthogClient: PostHogClient | null = null;
let initPromise: Promise<PostHogClient | null> | null = null;

/**
 * Paths to exclude from analytics. Admin pages are operator surfaces — we
 * don't want admin clicks polluting funnels, retention, or session replay.
 * Update this list if more internal-only routes are added.
 */
const EXCLUDED_PATH_PREFIXES = ['/admin'];

function isExcludedPath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return EXCLUDED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export async function initBrowserPostHog(): Promise<PostHogClient | null> {
  if (typeof window === 'undefined') return null;
  if (isExcludedPath()) return null;
  if (posthogClient) return posthogClient;
  if (!POSTHOG_BROWSER_KEY) return null;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const { default: posthog } = await import('posthog-js');
    posthog.init(POSTHOG_BROWSER_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: 'history_change',
      capture_pageleave: true,
      autocapture: {
        // Skip autocapture for end-user form fills — those are tracked
        // explicitly via answer_question / finished_conversation, and the
        // raw input contents are PII.
        css_selector_allowlist: [],
        dom_event_allowlist: ['click', 'submit', 'change'],
        element_allowlist: [
          'a',
          'button',
          'form',
          'input',
          'select',
          'textarea',
          'label',
        ],
      },
      person_profiles: 'identified_only',
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-no-capture] *, [data-private] *',
        recordCrossOriginIframes: false,
      },
      // Pull traits from window.__plura_traits if present so every event has
      // plan / lifecycle stage / onboarding traits even before identify().
      property_denylist: [],
      sanitize_properties: (properties) => {
        const traits = (typeof window !== 'undefined' && (window as unknown as { __plura_traits?: Record<string, unknown> }).__plura_traits) || {};
        return { ...traits, ...properties };
      },
      loaded: (ph) => {
        posthogClient = ph as PostHogClient;
      },
    });
    posthogClient = posthog as PostHogClient;
    return posthogClient;
  })();

  return initPromise;
}

export function getBrowserPostHog(): PostHogClient | null {
  return posthogClient;
}

export function browserCapture(
  event: string,
  properties: EventProperties = {},
  context?: Partial<CaptureContext>
): void {
  if (!isPostHogEnabled()) return;
  if (isExcludedPath()) return;
  const ph = posthogClient;
  if (!ph) {
    void initBrowserPostHog().then((client) => {
      client?.capture(event, sanitizeProps(properties, context));
    });
    return;
  }
  ph.capture(event, sanitizeProps(properties, context));
}

export function browserIdentify(traits: IdentifyTraits): void {
  if (!isPostHogEnabled()) return;
  if (isExcludedPath()) return;

  // Operator / internal account — suppress all PostHog activity for this
  // session. We also clear any previously-set traits so the next event
  // doesn't accidentally inherit them.
  if (isExcludedTrackingEmail(traits.email)) {
    if (typeof window !== 'undefined') {
      delete (window as unknown as { __plura_traits?: Record<string, unknown> }).__plura_traits;
    }
    const ph = posthogClient;
    if (ph && typeof ph.opt_out_capturing === 'function') {
      try {
        ph.opt_out_capturing();
      } catch {
        // best-effort; ignore SDK errors
      }
    }
    return;
  }

  const ph = posthogClient;
  const userProps = traitsToUserProps(traits);

  if (typeof window !== 'undefined') {
    (window as unknown as { __plura_traits?: Record<string, unknown> }).__plura_traits = {
      ...userProps,
    };
  }

  // If a previous session on this browser opted out (e.g. an excluded support
  // login), re-enable capturing for the current non-excluded user.
  if (ph && typeof ph.has_opted_out_capturing === 'function') {
    try {
      if (ph.has_opted_out_capturing()) ph.opt_in_capturing();
    } catch {
      // best-effort
    }
  }

  if (!ph) {
    void initBrowserPostHog().then((client) => {
      client?.identify(traits.userId, userProps);
    });
    return;
  }
  ph.identify(traits.userId, userProps);
}

export function browserReset(): void {
  posthogClient?.reset();
}

/**
 * Read a multivariate PostHog feature flag's variant for the current
 * user. Returns `null` when:
 *
 *   - PostHog isn't enabled / hasn't loaded yet
 *   - the path is excluded (e.g. `/admin`)
 *   - the flag isn't defined or evaluates to `false`
 *
 * Caller decides what to do on `null` — for experiments we fall back to
 * the slot's `control` variant so the user always sees a working UI.
 */
export function browserGetFeatureFlagVariant(flagKey: string): string | null {
  if (!isPostHogEnabled()) return null;
  if (isExcludedPath()) return null;
  const ph = posthogClient;
  if (!ph || typeof ph.getFeatureFlag !== 'function') return null;
  try {
    const value = ph.getFeatureFlag(flagKey);
    if (typeof value === 'string') return value;
    return null;
  } catch {
    return null;
  }
}

/**
 * Promise-based variant lookup that waits for PostHog flags to load if
 * they haven't yet. Useful for SSR-aware components that need a
 * deterministic variant before paint. Resolves `null` after `timeoutMs`
 * if PostHog is offline.
 */
export async function browserGetFeatureFlagVariantAsync(
  flagKey: string,
  timeoutMs = 800
): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  if (!isPostHogEnabled()) return null;
  if (isExcludedPath()) return null;

  const immediate = browserGetFeatureFlagVariant(flagKey);
  if (immediate) return immediate;

  await initBrowserPostHog();
  const ph = posthogClient;
  if (!ph) return null;

  // PostHog evaluates flags asynchronously after init. Poll for up to
  // `timeoutMs` so first-paint components can resolve their variant.
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };

    if (typeof ph.onFeatureFlags === 'function') {
      ph.onFeatureFlags(() => finish(browserGetFeatureFlagVariant(flagKey)));
    }
    setTimeout(() => finish(browserGetFeatureFlagVariant(flagKey)), timeoutMs);
  });
}

export function browserAlias(alias: string): void {
  posthogClient?.alias(alias);
}

function sanitizeProps(
  properties: EventProperties,
  context?: Partial<CaptureContext>
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...properties };
  if (context?.workspaceId) out['workspace_id'] = context.workspaceId;
  if (context?.stage) out['stage'] = context.stage;
  return out;
}

function traitsToUserProps(t: IdentifyTraits): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (t.email) out['email'] = t.email;
  if (t.name) out['name'] = t.name;
  if (t.workspaceId) out['workspace_id'] = t.workspaceId;
  if (t.workspaceName) out['workspace_name'] = t.workspaceName;
  if (t.plan !== undefined) out['plan'] = t.plan;
  if (t.isAdmin !== undefined) out['is_admin'] = t.isAdmin;
  if (t.signupAt) out['signup_at'] = new Date(t.signupAt).toISOString();
  if (t.signupSource) out['signup_source'] = t.signupSource;
  if (t.signupMedium) out['signup_medium'] = t.signupMedium;
  if (t.signupCampaign) out['signup_campaign'] = t.signupCampaign;
  if (t.signupLandingPage) out['signup_landing_page'] = t.signupLandingPage;
  if (t.signupReferrer) out['signup_referrer'] = t.signupReferrer;
  if (t.hearAboutUs) out['hear_about_us'] = t.hearAboutUs;
  if (t.lifecycleStage) out['lifecycle_stage'] = t.lifecycleStage;
  if (t.onboardingPurpose) out['onboarding_purpose'] = t.onboardingPurpose;
  if (t.onboardingRole) out['onboarding_role'] = t.onboardingRole;
  if (t.onboardingGoals) out['onboarding_goals'] = t.onboardingGoals;
  if (t.onboardingHasWebsite !== undefined)
    out['onboarding_has_website'] = t.onboardingHasWebsite;
  return out;
}
